import { Model, Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import {
  StockCount,
  StockCountLine,
} from "@towersystems/roam-common/lib/generated-types";
import { useState } from "react";
import StockCountModel from "../../../../utilities/data/model/StockCount";
import StockCountItemModel from "../../../../utilities/data/model/StockCountItem";
import StockCountLineModel from "../../../../utilities/data/model/StockCountLine";
import { stockCountDefinition } from "../../../../utilities/setup/definitions/stock-count-definition";
import { StockCountSyncContext } from "../../../../utilities/stock-count/context";
import {
  prepareCountItemEntity,
  prepareStockCountLineEntity,
  useCreateOrUpdateEntity,
  useGetChangedItemsByStockCountId,
} from "./utils";

export interface StockCountSyncProviderProps {
  children?: React.ReactNode;
}

export function StockCountSyncProvider({
  children,
}: StockCountSyncProviderProps) {
  const database = useDatabase();

  const { getChangedItemsByStockCountId } = useGetChangedItemsByStockCountId();
  const { createOrUpdateEntity } = useCreateOrUpdateEntity();
  const [loadingStockCountLines, setLoadingStockCountLines] =
    useState<boolean>(false);

  // this sync occurs on a per stocktake basis rather than running a full sync
  async function syncRemoteStockCount(stockCount: StockCount) {
    await createOrUpdateEntity<StockCount, StockCountModel>(
      stockCountDefinition.config.entity,
      stockCount,
      stockCountDefinition.config.prepareEntity
    );
  }

  async function syncRemoteStockCountLines(stockCountId: string) {
    try {
      setLoadingStockCountLines(true);
      await internalSyncRemoteStockCountLines(stockCountId);
    } catch (e) {
      throw e;
    } finally {
      setLoadingStockCountLines(false);
    }
  }

  async function internalSyncRemoteStockCountLines(stockCountId: string) {
    const lastFetch = Date.now();

    const { batchCreateActions, batchUpdateActions } =
      await getBatchActionsForStockCountLines(stockCountId);

    const stockCountModel = await database.collections
      .get<StockCountModel>("stock_counts")
      .find(stockCountId);

    await database.write(async () => {
      await database.batch([...batchCreateActions, ...batchUpdateActions]);
      await stockCountModel.update((model) => {
        model.itemsLastFetchMillis = lastFetch;
      });
    });
  }

  async function getBatchActionsForStockCountLines(stockCountId: string) {
    const batchCreateActions: Model[] = [];
    const batchUpdateActions: Model[] = [];

    const stockCountModel = await database.collections
      .get<StockCountModel>("stock_counts")
      .find(stockCountId);

    // only fetch items that have changed since previous fetch
    const items = await getChangedItemsByStockCountId(
      stockCountId,
      stockCountModel.itemsLastFetchMillis
        ? new Date(stockCountModel.itemsLastFetchMillis)
        : undefined
    );

    for (const item of items) {
      const lineBatchActions = await getLineBatchActions(item);
      batchCreateActions.push(...lineBatchActions.batchCreateActions);
      batchUpdateActions.push(...lineBatchActions.batchUpdateActions);
    }

    return { batchCreateActions, batchUpdateActions };
  }

  async function getLineBatchActions(line: StockCountLine) {
    const batchCreateActions: Model[] = [];
    const batchUpdateActions: Model[] = [];

    const stockCountLineCollection =
      database.collections.get<StockCountLineModel>("stock_count_lines");

    const andClause = line.productId
      ? Q.or(
          Q.where("product_id", line.productId),
          Q.where("barcode", line.barcode)
        )
      : Q.where("barcode", line.barcode);

    const stockCountLine = await stockCountLineCollection
      .query(Q.where("stock_count_id", line.stockCountId), Q.and(andClause))
      .fetch()
      .then((r) => r[0])
      .catch(() => undefined);

    if (!stockCountLine) {
      const createLineAction = stockCountLineCollection.prepareCreate(
        (newEntity) => prepareStockCountLineEntity(newEntity, line)
      );

      batchCreateActions.push(createLineAction);
    } else {
      const updateLineAction = stockCountLine.prepareUpdate((newEntity) =>
        prepareStockCountLineEntity(newEntity, line, { isUpdate: true })
      );

      batchUpdateActions.push(updateLineAction);
    }

    const countItemBatchActions = await getBatchActionsForCountItems(line);
    batchCreateActions.push(...countItemBatchActions.batchCreateActions);
    batchUpdateActions.push(...countItemBatchActions.batchUpdateActions);

    return { batchCreateActions, batchUpdateActions };
  }

  async function getBatchActionsForCountItems(line: StockCountLine) {
    const batchCreateActions: Model[] = [];
    const batchUpdateActions: Model[] = [];

    const countItemCollection =
      database.collections.get<StockCountItemModel>("stock_count_items");

    for (const remoteCountItem of line.items) {
      const matchedItems = await countItemCollection.query(
        Q.where("remote_id", remoteCountItem.id)
      );

      const countItem = matchedItems[0];

      if (!countItem) {
        const createCountItemAction = countItemCollection.prepareCreate(
          (newEntity) => prepareCountItemEntity(newEntity, remoteCountItem)
        );

        batchCreateActions.push(createCountItemAction);
      } else {
        const updateCountItemAction = countItem.prepareUpdate((newEntity) =>
          prepareCountItemEntity(newEntity, remoteCountItem, { isUpdate: true })
        );

        batchUpdateActions.push(updateCountItemAction);
      }
    }

    return {
      batchCreateActions,
      batchUpdateActions,
    };
  }

  const context = {
    syncRemoteStockCount,
    syncRemoteStockCountLines,
    loadingStockCountLines,
  };

  return (
    <StockCountSyncContext.Provider value={context}>
      {children}
    </StockCountSyncContext.Provider>
  );
}
